import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { num } from '@/lib/format'

interface PagerProps {
  page: number
  size: number
  total: number
  onPageChange: (page: number) => void
  onSizeChange: (size: number) => void
  /**
   * Row counts on offer. The default is the PO queue's; the Sales Order queue
   * passes its own because Vision defaults that endpoint to 20, and a select
   * whose options exclude the current value renders as the wrong one.
   */
  sizeOptions?: readonly number[]
}

/**
 * Table footer. Left: the "Showing x–y of z" count with the rows-per-page
 * picker tucked directly beneath it as underlined text, so the pair costs two
 * lines of 12px and no field chrome. Pagination sits centred on `sm` and up
 * and drops below the count on phones, where only the pages adjacent to the
 * current one stay visible (first/last and the gaps hide) so nine 44px touch
 * targets never have to fit a 360px screen.
 */
export function Pager({
  page,
  size,
  total,
  onPageChange,
  onSizeChange,
  sizeOptions = [25, 50, 100],
}: PagerProps) {
  const pages = Math.max(1, Math.ceil(total / size))
  const from = total === 0 ? 0 : page * size + 1
  const to = Math.min((page + 1) * size, total)
  const items = pageItems(page, pages)
  const nearCurrent = (item: number) => Math.abs(item - page) <= 1

  return (
    <div className="mt-3 grid grid-cols-1 items-center gap-3 text-[12px] text-[color:var(--muted-foreground)] sm:grid-cols-[1fr_auto_1fr]">
      <div className="flex flex-col items-start gap-0.5 justify-self-start">
        <div>
          Showing <span className="tabular-nums text-[color:var(--foreground)]">{num(from)}</span>–
          <span className="tabular-nums text-[color:var(--foreground)]">{num(to)}</span> of{' '}
          <span className="tabular-nums text-[color:var(--foreground)]">{num(total)}</span>
        </div>
        <Select
          value={String(size)}
          onValueChange={next => onSizeChange(Number.parseInt(next, 10))}
        >
          <SelectTrigger variant="text" className="tabular-nums" aria-label="Rows per page">
            {size} rows per page
          </SelectTrigger>
          <SelectContent align="start">
            {sizeOptions.map(option => (
              <SelectItem key={option} value={String(option)} className="tabular-nums">
                {option} rows per page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Pagination className="mx-0 w-auto justify-start sm:col-start-2 sm:justify-center">
        <PaginationContent className="flex-wrap">
          <PaginationItem>
            <PaginationPrevious disabled={page === 0} onClick={() => onPageChange(page - 1)} />
          </PaginationItem>
          {items.map((item, index) =>
            item === 'gap' ? (
              <PaginationItem key={`gap-${index}`} className="hidden sm:block">
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem
                key={item}
                className={nearCurrent(item) ? undefined : 'hidden sm:block'}
              >
                <PaginationLink
                  isActive={item === page}
                  aria-label={`Page ${item + 1}`}
                  onClick={() => onPageChange(item)}
                >
                  {item + 1}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationNext disabled={page + 1 >= pages} onClick={() => onPageChange(page + 1)} />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}

/**
 * Zero-based page numbers to show, with `'gap'` markers where pages are
 * elided. Always shows first, last, and a window of one either side of the
 * current page; never more than seven slots.
 */
export function pageItems(page: number, pages: number): Array<number | 'gap'> {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index)
  const last = pages - 1
  const window = new Set<number>([0, last, page - 1, page, page + 1])
  if (page <= 2) [1, 2, 3].forEach(p => window.add(p))
  if (page >= last - 2) [last - 1, last - 2, last - 3].forEach(p => window.add(p))
  const sorted = [...window].filter(p => p >= 0 && p <= last).sort((a, b) => a - b)
  const out: Array<number | 'gap'> = []
  for (let index = 0; index < sorted.length; index += 1) {
    const current = sorted[index]!
    const previous = sorted[index - 1]
    if (previous != null && current - previous > 1) out.push('gap')
    out.push(current)
  }
  return out
}
